import React from 'react'
import { useForm, Controller } from 'react-hook-form'
import Card from '../components/Card'
import { ERROR_DICT, errorDict } from '../utils/errorDict'
import TextField from '../components/TextField'
import {
  PHONE_VALIDATION_PATTERN,
  NAME_VALIDATION_PATTERN,
  EMAIL_VALIDATION_PATTERN
} from '../constants'
import Btn from '../components/Btn'
import { useAxios } from '../hooks/useAxios'
import ImageUpload from '../components/ImageUpload'
import { getUserOrganization } from '../api/getUserOrganization'
import { updateUserOrganization } from '../api/updateUserOrganization'
import { type Organization } from '../server/user/info/organization/get'
import { toast } from 'react-toastify'
import Skeleton from 'react-loading-skeleton'

function ProfileEditAboutOrganizationPage(): React.ReactElement {
  const { loading, fire, error } = useAxios()
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isDirty }
  } = useForm<any>({
    defaultValues: {
      organizationName: '',
      organizationINN: '',
      organizationPhone: '',
      organizationEmail: '',
      organizationSite: '',
      organizationTelegram: '',
      organizationVk: '',
      lastName: '',
      name: '',
      phone: '',
      email: '',
      file: null
    }
  })

  const { loading: submitLoading, fire: submitFire } = useAxios()

  React.useEffect(() => {
    void fire(getUserOrganization()).then(({ error, response }) => {
      if (error) {
        toast.error(error.message)
      } else {
        const data = response?.data ?? {}
        Object.keys(data).forEach((key) => {
          setValue(key, data[key])
        })
      }
    })
  }, [])

  const onSubmit = async (data: Organization): Promise<void> => {
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'file') {
        formData.append('file', value as File)
      } else {
        if (typeof value === 'object') {
          formData.append(key, JSON.stringify(value))
        } else {
          formData.append(key, value + '')
        }
      }
    })

    const { error } = await submitFire(updateUserOrganization(formData))

    if (error) {
      toast.error(error.message)
    } else {
      toast.success('Изменения успешно сохранены')
    }
  }

  return (
    <>
      {loading || !error ? (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Card>
            {loading ? (
              <Skeleton width={'25%'}></Skeleton>
            ) : (
              <header className={'card-head text-h3'}>Организация</header>
            )}

            <div className={'card-content'}>
              {loading ? (
                <Skeleton
                  count={4}
                  className={'u-flexGrow1'}
                  height={'6rem'}
                  style={{ marginBottom: '4rem' }}
                ></Skeleton>
              ) : (
                <>
                  <TextField
                    className={'u-flexGrow1'}
                    control={control}
                    rules={{
                      required: true,
                      pattern: {
                        value: NAME_VALIDATION_PATTERN,
                        message: ''
                      }
                    }}
                    hasError={!!errors.organizationName}
                    errorText={errorDict(errors.organizationName)}
                    name="organizationName"
                    label="Наименование организации"
                    required
                  ></TextField>

                  <div className={'row row-gap-20 row-mobile-none'}>
                    <TextField
                      className={'u-flexGrow1'}
                      control={control}
                      hasError={!!errors.organizationInn}
                      errorText={errorDict(errors.organizationInn)}
                      name="organizationINN"
                      type="text"
                      label="ИНН"
                    ></TextField>
                    <TextField
                      className={'u-flexGrow1'}
                      mask={'+7 (999) 999-99-99'}
                      control={control}
                      rules={{
                        pattern: {
                          value: PHONE_VALIDATION_PATTERN,
                          message: ''
                        }
                      }}
                      hasError={!!errors.phone}
                      errorText={
                        errors.organizationPhone?.type === 'pattern'
                          ? ERROR_DICT.organizationPhone
                          : errorDict(errors.organizationPhone)
                      }
                      name="organizationPhone"
                      type="tel"
                      label="Телефон"
                    ></TextField>
                  </div>

                  <div className={'row row-gap-20 row-mobile-none'}>
                    <TextField
                      className={'u-flexGrow1'}
                      control={control}
                      name="organizationEmail"
                      type="email"
                      label="Электронная почта"
                      rules={{
                        pattern: {
                          value: EMAIL_VALIDATION_PATTERN,
                          message: ''
                        }
                      }}
                      errorText={
                        errors.organizationEmail?.type === 'pattern'
                          ? ERROR_DICT.organizationEmail
                          : errorDict(errors.organizationEmail)
                      }
                    ></TextField>
                    <TextField
                      className={'u-flexGrow1'}
                      control={control}
                      hasError={!!errors.organizationSite}
                      errorText={errorDict(errors.organizationSite)}
                      name="organizationSite"
                      type="text"
                      label="Сайт"
                    ></TextField>
                  </div>

                  <div className={'row row-gap-20 row-mobile-none'}>
                    <TextField
                      className={'u-flexGrow1'}
                      control={control}
                      hasError={!!errors.organizationTelegram}
                      errorText={errorDict(errors.organizationTelegram)}
                      name="organizationTelegram"
                      type="text"
                      label="Telegram"
                    ></TextField>
                    <TextField
                      className={'u-flexGrow1'}
                      control={control}
                      hasError={!!errors.organizationVk}
                      errorText={errorDict(errors.organizationVk)}
                      name="organizationVk"
                      type="text"
                      label="Vk"
                    ></TextField>
                  </div>
                </>
              )}
            </div>
          </Card>
          <Card className={'mt-md-5 mt-sm-4'}>
            {loading ? (
              <Skeleton width={'25%'}></Skeleton>
            ) : (
              <header className={'card-head text-h3'}>Контактное лицо</header>
            )}
            <div className={'card-content'}>
              {loading ? (
                <Skeleton
                  count={2}
                  className={'u-flexGrow1'}
                  height={'6rem'}
                  style={{ marginBottom: '4rem' }}
                ></Skeleton>
              ) : (
                <>
                  <div className={'row row-gap-20 row-mobile-none'}>
                    <TextField
                      className={'u-flexGrow1'}
                      control={control}
                      rules={{
                        pattern: {
                          value: NAME_VALIDATION_PATTERN,
                          message: ''
                        }
                      }}
                      hasError={!!errors.lastName}
                      errorText={errorDict(errors.lastName)}
                      name="lastName"
                      label="Фамилия"
                    ></TextField>
                    <TextField
                      className={'u-flexGrow1'}
                      control={control}
                      rules={{
                        pattern: {
                          value: NAME_VALIDATION_PATTERN,
                          message: ''
                        }
                      }}
                      hasError={!!errors.name}
                      errorText={errorDict(errors.name)}
                      name="name"
                      label="Имя"
                    ></TextField>
                  </div>
                  <div className={'row row-gap-20 row-mobile-none'}>
                    <TextField
                      className={'u-flexGrow1'}
                      mask={'+7 (999) 999-99-99'}
                      control={control}
                      rules={{
                        pattern: {
                          value: PHONE_VALIDATION_PATTERN,
                          message: ''
                        }
                      }}
                      hasError={!!errors.phone}
                      errorText={
                        errors.phone?.type === 'pattern'
                          ? ERROR_DICT.phone
                          : errorDict(errors.phone)
                      }
                      name="phone"
                      type="tel"
                      label="Телефон"
                    ></TextField>
                    <TextField
                      className={'u-flexGrow1'}
                      control={control}
                      name="email"
                      type="email"
                      label="Электронная почта"
                      rules={{
                        pattern: {
                          value: EMAIL_VALIDATION_PATTERN,
                          message: ''
                        }
                      }}
                      errorText={
                        errors.email?.type === 'pattern'
                          ? ERROR_DICT.email
                          : errorDict(errors.email)
                      }
                    ></TextField>
                  </div>
                </>
              )}
            </div>
          </Card>

          <Card className={'mt-md-5 mt-sm-4'}>
            {loading ? (
              <Skeleton width={'25%'}></Skeleton>
            ) : (
              <header className={'card-head text-h3'}>
                Логотип или изображение
              </header>
            )}
            <header className={'card-head text-h3'}></header>

            <div className={'card-content'}>
              <Controller
                control={control}
                name={'file'}
                rules={{ required: false }}
                render={({ field: { value, onChange } }) => (
                  <ImageUpload file={value} onChangeFile={onChange} />
                )}
              ></Controller>
            </div>
          </Card>

          <Btn
            className={'mt-md-8 mt-sm-6'}
            disabled={!isDirty || submitLoading}
            loading={submitLoading}
            type={'submit'}
          >
            Сохранить изменения
          </Btn>
        </form>
      ) : (
        <>{error.message}</>
      )}
    </>
  )
}

export default ProfileEditAboutOrganizationPage
